<script setup lang="ts">
import type { CrashGameName } from '~/types/crash-pre-game';

type Props = {
  /** `game_name` da whitelist (aviator | aviator-2). */
  gameName: CrashGameName;
};

const { gameName } = defineProps<Props>();

// Mesmo poller dos outros blocos da tela (uma request por tick).
const { data } = useCrashPreGame(() => gameName);

const { showMoneyFormat } = useWallet();

// `paidToday` é o único campo do payload que o core ainda devolve SEMPRE null —
// não existe fonte de payout ligada. E null aqui não é zero: renderizar
// "R$ 0,00" afirmaria que o jogo não pagou nada hoje, o que é informação falsa
// sobre o jogo. Então o bloco inteiro fica fora da tela até o dado existir.
//
// Nada a virar no front quando o back ligar: o valor aparece sozinho no
// primeiro tick que vier com número.
const formattedValue = computed(() => {
  const paidToday = data.value?.paidToday;

  // Zero cai no mesmo lugar que null, e de propósito: "R$ 0" é exatamente a
  // afirmação falsa que o bloco existe pra não fazer. Um jogo com o coletor no
  // ar e nenhum payout somado ainda não pagou nada — só não temos o número.
  if (typeof paidToday !== 'number' || paidToday <= 0) return null;

  // Sem centavos, como nos cards do catálogo: o número é grande e a casa
  // decimal só rouba espaço da linha.
  return showMoneyFormat({ value: paidToday, withCents: false });
});
</script>

<template>
  <div
    v-if="formattedValue"
    class="crash-paid"
  >
    <NuxtIcon
      class="crash-paid__coin"
      filled
      name="filter/coin"
    />

    <div class="crash-paid__text">
      <span class="crash-paid__label">Pagou hoje</span>
      <span class="crash-paid__value">{{ formattedValue }}</span>
    </div>
  </div>
</template>

<style scoped>
.crash-paid {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.crash-paid__coin {
  display: inline-flex;
  font-size: 22px;
  line-height: 1;
}

.crash-paid__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.crash-paid__label {
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.crash-paid__value {
  color: #ffd84d;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
</style>
